import {useCallback, useMemo} from "react"

import {useAtom, useAtomValue} from "jotai"
import {queryClientAtom} from "jotai-tanstack-query"

import {
    searchQueryAtom,
    traceTabsAtom,
    filtersAtom,
    sortAtom,
    selectedTraceIdAtom,
    selectedRowKeysAtom,
    isAnnotationsSectionOpenAtom,
    selectedNodeAtom,
    DEFAULT_SORT,
    limitAtom,
    autoRefreshAtom,
} from "../state/controls"
import {
    tracesQueryAtom,
    traceCountAtom,
    annotationsQueryAtom,
    tracesWithAnnotationsAtom,
    observabilityLoadingAtom,
    activeTraceIndexAtom,
    activeTraceAtom,
    selectedItemAtom,
} from "../state/queries"

export const useObservability = () => {
    const [searchQuery, setSearchQuery] = useAtom(searchQueryAtom)
    const [traceTabs, setTraceTabs] = useAtom(traceTabsAtom)
    const [filters, setFilters] = useAtom(filtersAtom)
    const [sort, setSort] = useAtom(sortAtom)
    const [selectedTraceId, setSelectedTraceId] = useAtom(selectedTraceIdAtom)
    const [selectedRowKeys, setSelectedRowKeys] = useAtom(selectedRowKeysAtom)
    const [isAnnotationsSectionOpen, setIsAnnotationsSectionOpen] = useAtom(
        isAnnotationsSectionOpenAtom,
    )
    const [selectedNode, setSelectedNode] = useAtom(selectedNodeAtom)
    const [limit] = useAtom(limitAtom)
    const [autoRefresh, setAutoRefresh] = useAtom(autoRefreshAtom)

    const [
        {
            refetch: refetchTraces,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            isLoading: isLoadingTraces,
            isFetching: isFetchingTraces,
            isRefetching: isRefetchingTraces,
            isError: isTracesError,
            error: tracesError,
        },
    ] = useAtom(tracesQueryAtom)

    const [
        {
            data: annotationsData,
            refetch: refetchAnnotations,
            isLoading: isLoadingAnnotations,
            isFetching: isFetchingAnnotations,
            isRefetching: isRefetchingAnnotations,
        },
    ] = useAtom(annotationsQueryAtom)

    const isLoadingObservability = useAtomValue(observabilityLoadingAtom)

    const traces = useAtomValue(tracesWithAnnotationsAtom)
    const traceCount = useAtomValue(traceCountAtom)
    const activeTraceIndex = useAtomValue(activeTraceIndexAtom)
    const activeTrace = useAtomValue(activeTraceAtom)
    const selectedItem = useAtomValue(selectedItemAtom)
    const annotations = annotationsData ?? []

    const isRefreshing = useMemo(
        () =>
            isFetchingTraces ||
            isFetchingAnnotations ||
            isRefetchingTraces ||
            isRefetchingAnnotations,
        [isFetchingTraces, isFetchingAnnotations, isRefetchingTraces, isRefetchingAnnotations],
    )

    const isRateLimited = isTracesError && (tracesError as {status?: number})?.status === 429
    const rateLimitMessage = isRateLimited
        ? (tracesError as Error)?.message || "You have reached your monthly quota limit."
        : undefined

    const fetchTraces = useCallback(async () => {
        const res = await refetchTraces()
        return res.data
    }, [refetchTraces])

    const fetchAnnotations = useCallback(async () => {
        const res = await refetchAnnotations()
        return res.data
    }, [refetchAnnotations])

    const fetchMoreTraces = useCallback(async () => {
        if (!hasNextPage) return []
        const res = await fetchNextPage()
        const pages = res.data?.pages || []
        return pages.length ? pages[pages.length - 1].traces : []
    }, [fetchNextPage, hasNextPage])

    const queryClient = useAtomValue(queryClientAtom)

    /**
     * Resets the traces infinite query to its initial state (page 1 only).
     * TanStack Query clears the cache and re-fetches page 1 on next render.
     * Use this for auto-refresh so N loaded pages don't cause N API calls.
     */
    const resetTracePages = useCallback(() => {
        queryClient.resetQueries({queryKey: ["traces"]})
    }, [queryClient])

    const clearQueryStates = useCallback(() => {
        setSearchQuery("")
        setTraceTabs("trace")
        setFilters([])
        setSort(DEFAULT_SORT)
    }, [setSearchQuery, setTraceTabs, setFilters, setSort])

    return {
        traces,
        traceCount,
        annotations,
        isLoading:
            isLoadingObservability || isLoadingTraces || isLoadingAnnotations || isRefreshing,
        isRateLimited,
        rateLimitMessage,
        fetchMoreTraces,
        hasMoreTraces: hasNextPage,
        isFetchingMore: isFetchingNextPage,
        fetchTraces,
        fetchAnnotations,
        resetTracePages,
        clearQueryStates,
        searchQuery,
        setSearchQuery,
        traceTabs,
        setTraceTabs,
        filters,
        setFilters,
        sort,
        setSort,
        selectedTraceId,
        setSelectedTraceId,
        limit,
        selectedRowKeys,
        setSelectedRowKeys,
        isAnnotationsSectionOpen,
        setIsAnnotationsSectionOpen,
        selectedNode,
        setSelectedNode,
        activeTraceIndex,
        activeTrace,
        selectedItem,
        autoRefresh,
        setAutoRefresh,
    }
}
